package com.nonopn.skywatcher.common.app.pages.map.model.weather

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class ThreatsPeriod(
    val timestamp: Long,
    @SerialName("storms")
    val storm: Storm? = null
)

@Serializable
data class Storm(
    val phrase: StormPhrase,
    val distance: StormDistance,
    /**
     * Object of the direction information of the threats from the requested location.
     */
    val direction: StormDirection,
    /**
     * True if the storms are approaching, False is passing through.
     */
    val approaching: Boolean,
    /**
     * Object of the speed information of the threats from the requested location.
     */
    val speed: StormSpeed,
    /**
     * An arc span angle of the storm threats affecting a location. If a storm threat is approaching from the WSW at 256 degrees. If Span = 75, then an arc can be drawn that is 75 degrees and is centered on the 256 degree direction (WSW) to show the area the storms are approaching from. Value will be 0 if outside of US.
     */
    val span: Int,
    /**
     * Object of hail information for the approaching storm threats. Null if in non-US location or no hail within the storm threats.
     */
    val hail: StormHail? = null,
    /**
     * True if the approaching storm threats include a decent amount of mesoscale rotation, false otherwise. Value will be false if outside of US.
     */
    val rotation: Boolean,
    /**
     * True if the approaching storm threats may be tornadic. Value will be false if outside of the US.
     */
    val tornadic: Boolean,
    /**
     * An array of active severe storm related NOAA advisory VTEC codes.
     *
     * If the location is under a severe thunderstorm warnings the value of ['SV.W']. If under both a Severe thunderstorm warning and Tornado warning the value may be ['TO.W', 'SV.W']. Refer to the advisories endpoint for more information on the advisory VTEC codes.
     *
     * Null if no severe related storm advisories or outside the US.
     */
    val advisories: List<String>? = null,
    val mda: StormMDA? = null,
    val dbz: StormDBZ,
    val lightning: StormLightning? = null
)

@Serializable
data class StormPhrase(
    /**
     * Longer phrase of storm threats. Perfect for medium size displays
     */
    val long: String,
    /**
     * A shortened phrase of storm threats. Perfect for mobile phones where space is a limitation.
     */
    val short: String
)

@Serializable
data class StormDistance(
    /**
     * The minimum distance, in kilometers, of the group of storm threats from the specified location.
     */
    val minKM: Double,
    /**
     * The maximum distance, in kilometers, of the group of storm threats from the specified location.
     */
    val maxKM: Double,
    /**
     * The average distance, in kilometers, of the group of storm threats from the specified location.
     */
    val avgKM: Double
)

/**
 * Object of the direction information of the threats from the requested location.
 */
@Serializable
data class StormDirection(
    /**
     * The direction, in degrees, of the group of storm threats from the specified location. 0-359, where 0 is North.
     */
    val toDEG: Int,
    /**
     * The abbreviated direction from the specified location to the storm threats. N, NNE, NE, ENE, E, ESE, SE, SSE, S, SSW, SW, WSW, W, WNW, NW, NNW.
     */
    val to: StormDirectionAbbreviated,
    /**
     * The direction, in degrees, the threats approaching from. 0-359, where 0 is North.
     */
    val fromDEG: Int,
    /**
     * The abbreviated direction the threats are approaching from. N, NNE, NE, ENE, E, ESE, SE, SSE, S, SSW, SW, WSW, W, WNW, NW, NNW.
     */
    val from: StormDirectionAbbreviated
)

/**
 * The abbreviated direction the threats are approaching from. N, NNE, NE, ENE, E, ESE, SE, SSE, S, SSW, SW, WSW, W, WNW, NW, NNW.
 */
@Serializable
enum class StormDirectionAbbreviated {
    N, NNE, NE, ENE, E, ESE, SE, SSE, S, SSW, SW, WSW, W, WNW, NW, NNW
}

/**
 * Object of the speed information of the threats from the requested location.
 */
@Serializable
data class StormSpeed(
    /**
     * The minimum speed of the group of storms threats in kilometers per hour
     */
    val minKPH: Float,
    /**
     * The maximum speed of the group of storms threats in kilometers per hour
     */
    val maxKPH: Float,
    /**
     * The average speed of the group of storms threats in kilometers per hour
     */
    val avgKPH: Float
)

/**
 * Object of hail information for the approaching storm threats. Null if in non-US location or no hail within the storm threats.
 */
@Serializable
data class StormHail(
    /**
     * The probability (0-100) that the storm threats include hail.
     */
    val prob: Float,
    /**
     * The probability (0-100) that the storm threats include severe hail. Severe hail is defined as 1 inch or larger.
     */
    val probSevere: Float,
    /**
     * The average hail size, in millimeters, of the storm threats.
     */
    val maxSizeMM: Int
)

/**
 * Object of mesoscale rotation information for the storm threats. Null if outside of the US or no appreciable MDA for the approaching storms
 */
@Serializable
data class StormMDA(
    /**
     * The minimum mesoscale rotation value of the storm threats.
     */
    val min: Int,
    /**
     * The maximum mesoscale rotation value of the storm threats.
     */
    val max: Int,

    )

@Serializable
data class StormDBZ(
    val min: Double,
    val max: Double,
    val avg: Double
)

@Serializable
data class StormLightning(
    /**
     * Number of impact aroudn 10Km
     */
    val nearby: Int,
    /**
     * Number of impacts approaching
     */
    val approaching: Int,
    val phrase: String
)

val Storm.Companion.fake: Storm
    get() = Storm(
        phrase = StormPhrase("", ""),
        distance = StormDistance(
            minKM = 50.0,
            maxKM = 80.0,
            avgKM = 60.0
        ),
        direction = StormDirection(
            toDEG = 20,
            fromDEG = 50,
            to = StormDirectionAbbreviated.ENE,
            from = StormDirectionAbbreviated.ESE
        ),
        approaching = true,
        speed = StormSpeed(
            minKPH = 100.0f,
            maxKPH = 200.0f,
            avgKPH = 150.0f
        ),
        span = 0,
        hail = StormHail(
            prob = 0.5f,
            probSevere = 0.9f,
            maxSizeMM = 100
        ),
        rotation = false,
        tornadic = false,
        advisories = emptyList(),
        mda = StormMDA(
            min = 1,
            max = 6
        ),
        dbz = StormDBZ(
            min = 1.0,
            max = 10.0,
            avg = 5.0
        )
    )